/**
 * ACIP catch-up schedule (simplified)
 * ───────────────────────────────────────────────────────────
 * Implements the age-based "doses expected by" milestone view
 * of the CDC/ACIP childhood immunization schedule. Given patient
 * age in months + doses received per antigen, returns per-antigen
 * status (up_to_date / due_now / catch_up) + a plain-English
 * next-step message.
 *
 * Reference: CDC Child & Adolescent Immunization Schedule 2024.
 * Tables encoded for ages 2–60 months, which covers the primary
 * pediatric scheduling window. For older patients, extend the
 * MILESTONES map.
 */

/* Minimum doses expected to be complete by a given age. */
const MILESTONES_BY_AGE = [
  // ageMonths, minimum doses per antigen
  { age: 2,  doses: { hepb: 1, dtap: 0, hib: 0, pcv13: 0, ipv: 0, rota: 0 } },
  { age: 4,  doses: { hepb: 2, dtap: 1, hib: 1, pcv13: 1, ipv: 1, rota: 1 } },
  { age: 6,  doses: { hepb: 2, dtap: 2, hib: 2, pcv13: 2, ipv: 2, rota: 2 } },
  { age: 9,  doses: { hepb: 2, dtap: 3, hib: 2, pcv13: 3, ipv: 2, rota: 3 } },
  { age: 12, doses: { hepb: 3, dtap: 3, hib: 3, pcv13: 3, ipv: 2, rota: 3 } },
  { age: 15, doses: { hepb: 3, dtap: 3, hib: 3, pcv13: 4, ipv: 3, mmr: 1, var: 1, hepa: 1, rota: 3 } },
  { age: 18, doses: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, mmr: 1, var: 1, hepa: 1, rota: 3 } },
  { age: 24, doses: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, mmr: 1, var: 1, hepa: 2, rota: 3 } },
  { age: 36, doses: { hepb: 3, dtap: 4, hib: 4, pcv13: 4, ipv: 3, mmr: 1, var: 1, hepa: 2, rota: 3 } },
  { age: 48, doses: { hepb: 3, dtap: 5, hib: 4, pcv13: 4, ipv: 4, mmr: 2, var: 2, hepa: 2, rota: 3 } },
  { age: 60, doses: { hepb: 3, dtap: 5, hib: 4, pcv13: 4, ipv: 4, mmr: 2, var: 2, hepa: 2, rota: 3 } },
];

export const ANTIGENS = {
  hepb:  { label: 'Hep B',     max: 3, min_interval_weeks: 8  },
  dtap:  { label: 'DTaP',      max: 5, min_interval_weeks: 4  },
  hib:   { label: 'Hib',       max: 4, min_interval_weeks: 4  },
  pcv13: { label: 'PCV13',     max: 4, min_interval_weeks: 4  },
  ipv:   { label: 'IPV',       max: 4, min_interval_weeks: 4  },
  mmr:   { label: 'MMR',       max: 2, min_interval_weeks: 4  },
  var:   { label: 'Varicella', max: 2, min_interval_weeks: 12 },
  hepa:  { label: 'Hep A',     max: 2, min_interval_weeks: 24 },
  rota:  { label: 'Rotavirus', max: 3, min_interval_weeks: 4  },
  flu:   { label: 'Flu (seasonal)', max: 1, min_interval_weeks: 0 }, // annual from 6 mo
};

/* Return expected dose count for a given antigen at `ageMonths`. */
const expectedDoses = (ageMonths, antigen) => {
  let expected = 0;
  for (const m of MILESTONES_BY_AGE) {
    if (ageMonths >= m.age) {
      const v = m.doses[antigen];
      if (typeof v === 'number') expected = v;
    }
  }
  return expected;
};

/**
 * Compute ACIP status for a patient.
 *   ageMonths : integer age in months
 *   received  : { antigenId: dosesGiven }
 *
 * Returns an array of:
 *   { id, label, received, expected, status, message }
 *   status ∈ 'complete' | 'up_to_date' | 'due_now' | 'catch_up'
 */
export const acipAssessment = (ageMonths, received = {}) => {
  const items = [];
  for (const [id, meta] of Object.entries(ANTIGENS)) {
    const got = received[id] || 0;
    const exp = expectedDoses(ageMonths, id);
    let status = 'up_to_date';
    let message = '';
    if (got >= meta.max) {
      status = 'complete';
      message = 'Series complete.';
    } else if (got < exp) {
      const behind = exp - got;
      status = got === 0 && exp >= 2 ? 'catch_up' : 'due_now';
      message = status === 'catch_up'
        ? `Catch-up needed — ${behind} doses behind schedule.`
        : `Dose #${got + 1} due today (${behind} behind).`;
    } else if (got === exp && got < meta.max) {
      // On schedule — next dose will come at a later milestone.
      status = 'up_to_date';
      message = `Dose #${got + 1} scheduled at next milestone.`;
    }
    items.push({ id, label: meta.label, received: got, expected: exp, max: meta.max, status, message });
  }
  // Flu (seasonal) — always "due" after 6 mo if not given this season.
  if (ageMonths >= 6) {
    const flu = items.find((i) => i.id === 'flu');
    if (flu && flu.received === 0) {
      flu.status = 'due_now';
      flu.message = 'Annual influenza vaccine recommended.';
    }
  }
  return items;
};

export default acipAssessment;
